import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

export default function Postings() {
  const { apiFetch } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingEntries, setLoadingEntries] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    apiFetch('/api/accounts')
      .then(accs => {
        setAccounts(accs);
        setLoading(false);
      })
      .catch(err => { setError(err.message); setLoading(false); });
  }, [apiFetch]);

  useEffect(() => {
    if (!selectedAccountId) { setData(null); return; }
    setLoadingEntries(true);
    setError('');
    apiFetch(`/api/reports/ledger/${selectedAccountId}`)
      .then(d => { setData(d); setLoadingEntries(false); })
      .catch(err => { setError(err.message); setLoadingEntries(false); });
  }, [selectedAccountId, apiFetch]);

  const fmt = (n) => {
    if (!n || n === 0) return '';
    return Number(n).toLocaleString('da-DK', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return date.toLocaleDateString('da-DK');
  };

  const runningBalance = useMemo(() => {
    if (!data?.entries || !data?.account) return [];
    const isDebitNormal = ['asset', 'expense'].includes(data.account.type);
    let balance = 0;
    return data.entries.map(entry => {
      const debit = Number(entry.debit) || 0;
      const credit = Number(entry.credit) || 0;
      balance += isDebitNormal ? (debit - credit) : (credit - debit);
      return { ...entry, balance };
    });
  }, [data]);

  const totalDebit = useMemo(() => runningBalance.reduce((sum, e) => sum + (Number(e.debit) || 0), 0), [runningBalance]);
  const totalCredit = useMemo(() => runningBalance.reduce((sum, e) => sum + (Number(e.credit) || 0), 0), [runningBalance]);

  const sortedAccounts = useMemo(() => {
    const typeOrder = { asset: 0, liability: 1, equity: 2, income: 3, expense: 4 };
    return [...accounts].sort((a, b) => {
      const ta = typeOrder[a.type] ?? 5;
      const tb = typeOrder[b.type] ?? 5;
      if (ta !== tb) return ta - tb;
      return a.account_number.localeCompare(b.account_number);
    });
  }, [accounts]);

  if (loading) return <div className="loading">Indlæser...</div>;

  return (
    <div>
      <div className="page-header">
        <h1>Posteringer</h1>
      </div>

      <div className="card" style={{marginBottom: '1.5rem'}}>
        <div className="form-group" style={{marginBottom: 0}}>
          <label className="form-label">Vælg konto</label>
          <select
            className="form-control"
            value={selectedAccountId}
            onChange={e => setSelectedAccountId(e.target.value)}
          >
            <option value="">-- Vælg en konto --</option>
            {sortedAccounts.map(a => (
              <option key={a.id} value={a.id}>
                {a.account_number} – {a.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {loadingEntries && <div className="loading">Indlæser posteringer...</div>}

      {data && !loadingEntries && (
        <>
          {data.account && (
            <div style={{marginBottom: '1rem', color: 'var(--gray-600)', fontSize: '0.9rem'}}>
              Konto {data.account.account_number} – {data.account.name} ({
                {asset: 'Aktiv', liability: 'Passiv', equity: 'Egenkapital', income: 'Indt\u00e6gt', expense: 'Udgift'}[data.account.type] || data.account.type
              })
            </div>
          )}

          {runningBalance.length === 0 ? (
            <div className="card empty-state">
              <p>Ingen posteringer på denne konto.</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table">
                <thead>
                  <tr>
                    <th>Dato</th>
                    <th>Bilag</th>
                    <th>Tekst</th>
                    <th style={{textAlign: 'right'}}>Debet</th>
                    <th style={{textAlign: 'right'}}>Kredit</th>
                    <th style={{textAlign: 'right'}}>Saldo</th>
                  </tr>
                </thead>
                <tbody>
                  {runningBalance.map((entry, i) => (
                    <tr key={i}>
                      <td>{fmtDate(entry.date)}</td>
                      <td>
                        <Link to={`/app/bilag/${entry.voucher_id}`}>
                          {entry.voucher_number}
                        </Link>
                      </td>
                      <td>{entry.description || entry.voucher_description}</td>
                      <td style={{textAlign: 'right'}}>{fmt(entry.debit)}</td>
                      <td style={{textAlign: 'right'}}>{fmt(entry.credit)}</td>
                      <td style={{textAlign: 'right', fontWeight: 600}}>{fmt(entry.balance)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{fontWeight: 700, borderTop: '2px solid var(--gray-300)'}}>
                    <td colSpan={3}>Total</td>
                    <td style={{textAlign: 'right'}}>{fmt(totalDebit)}</td>
                    <td style={{textAlign: 'right'}}>{fmt(totalCredit)}</td>
                    <td style={{textAlign: 'right'}}>{fmt(runningBalance[runningBalance.length - 1]?.balance)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}
